<template>
    <div class="overflow-hidden relative" :class="[fullRounded ? 'rounded-full' : (props.tile ? null : 'rounded-sm')]" :style="{width: `${props.size}px`, backgroundColor: props.color, minWidth: props.cover ? '100%' : `${props.size}px`}">
        <cdn-image :src="imageSource" :alt="altName" :width="props.size" :ratio="aspectRatio" :cover="props.cover" :rounded-full="fullRounded" :rounded-small="!fullRounded && !props.tile" :immediate="immediate"/>
        <slot/>
    </div>
</template>

<script setup lang="ts">
import {DefaultAvatar} from "@spoferan/spoferan-ts-core";
import {computed} from 'vue';
import type {Image, MainModelPreview} from "@spoferan/spoferan-ts-core";

const props = withDefaults(defineProps<{
	// The model to display the avatar for
	model?: MainModelPreview | null,

	// The image source to display the avatar for
	src?: string | Image,

	// The fallback source to display if no model or src is given
	fallback?: string,

	// The width of the avatar
	size?: number,

	// The ratio of the avatar
	ratio?: number,

	// Whether to scale the image as large as possible to fill the container, stretching the image if necessary.
	// If the proportions of the image differ from the element, it is cropped either vertically or horizontally so that no empty space remains.
	cover?: boolean,

	// Whether no border radius shall be applied
	tile?: boolean,

	// Whether to immediate load the image when the component is loaded even when it's not visible.
	// Also adds a preload link on the page head.
	// This might be useful to optimize the page for LCP.
	immediate?: boolean,

	// Whether a full border radius shall be applied
	round?: boolean,

	// The background fill color of the image, especially for PNGs with transparent background
	color?: string,

	// The alternative text to display on the image for screen readers
	alt?: string
}>(), {
	fallback: DefaultAvatar
});

const aspectRatio = computed(() => {
	if (props.ratio) {
		return props.ratio;
	}
	if (props.model?.avatar_thumbnail?.ratio) {
		return props.model.avatar_thumbnail.ratio
	}

	return props.model && props.model.model_name === 'event' ? 3/2 : 1;
});

const altName = computed(() => {
	return props.alt ?? (props.model ? props.model.display_name : undefined);
});

const fullRounded = computed((): boolean => {
	return ((props.model && ['user', 'member', 'user_job'].includes(props.model.model_name) && !props.tile) || props.round)
});

const imageSource = computed(() => {
	if (props.model && props.model.avatar_thumbnail) {
		return props.model.avatar_thumbnail
	}

	if (props.src) {
		return props.src
	}

	return props.fallback;
});

</script>